import { logEvent } from 'firebase/analytics';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search } from 'react-feather';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { myAnalytics, myRealtimeDB } from '../../../../firebase/config';
import {
  resetInvoiceIdsForDownload,
  updateBuyerSupplierInvoicesDataWeeksLimit,
  selectInvoiceIdsForDownload,
  unSelectInvoiceIdsForDownload,
} from '../../../../redux/globalSlices/buyersSlice';
import {
  updateSelectedSupplierForBuyer,
  resetSelectInvoice,
  selectInvoice,
  selectCreditMemo,
  unSelectInvoice,
  unSelectCreditMemo,
} from '../../../../redux/globalSlices/invoiceSlice';
import {
  downloadMultiInvoicePdfZip,
  getGroupedBuyersSuppliers,
  getInvoicePdf,
} from '../../../../redux/services/buyersService';
import { mainBuyerDownloadSupplierInvoiceStatements } from '../../../../redux/services/invoicesService';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { COLORS } from '../../../../utils/colors';
import { weeksOptions } from '../../../../utils/constants';
import {
  InvoiceStatus,
  QuickbooksInvoice,
  Supplier,
  roundTo,
  QuickbooksJob,
  removeDuplicatesByProperty,
  SupplierForBuyer,
  QuickbooksCreditMemo,
  QuickbooksInvoiceSchema,
  QuickbooksCreditMemoSchema,
  formatMoney,
  DEFAULT_AGING_RANGES,
  calculateAgingBucket,
  assignCreditsToAgingBuckets,
  isCreditMemoSelectable,
} from '@dill/dill-shared';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppSelectedPayInvoicesModal from '../../../general/AppSelectedPayInvoicesModal/AppSelectedPayInvoicesModal';
import { InvoicesTable } from '../../invoices/InvoicesTable/InvoicesTable';
import AppLastSyncDataView from '../../../general/AppLastSyncDataView/AppLastSyncDataView';
import AppViewInvoiceWithoutPdfModal from '../../../general/AppViewInvoiceWithoutPdfModal/AppViewInvoiceWithoutPdfModal';
import { onValue, ref } from 'firebase/database';
import type { Unsubscribe } from 'firebase/database';
import { twMerge } from 'tailwind-merge';
import { CurrencyCircleDollar, Clock, Buildings, X, FilePlus } from 'phosphor-react';
import { updateLastSyncTimeBuyer } from '../../../../redux/globalSlices/genericSlice';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { getUserByBuyers } from '../../../../redux/services/authService';
import { downloadFile, openDillToast } from '../../../../utils/helpers';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useWindowActualWidth } from '../../../../utils/domTools';
import { DillButton, DillCheckbox, DillDropdownButton, DillDropdown } from '../../../general/Dill';
import { useBuyerUserPermissions } from '../../../../utils/hooks/useBuyerUserPermissions';
import { ViewSupplierInvoicesMobile } from './ViewSupplierInvoicesMobile';
import { getScheduledPayments } from '../../../../redux/services/paymentsService';

export interface InvoiceTabs {
  id: InvoiceStatus;
  name: string;
  totalAmount: number;
  pastDueCount?: number;
  invoices: (QuickbooksInvoice | QuickbooksCreditMemo)[];
  credits: QuickbooksCreditMemo[];
}
export const ViewSupplierInvoices = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const width = useWindowActualWidth();
  const {
    selectedBuyerSuppliers,
    invoiceIdsForDownload,
    loadingList: buyersLoadingList,
    buyerSupplierInvoicesDataWeeksLimit,
  } = useAppSelector((state) => state.buyers);
  const { user } = useAppSelector((state) => state.auth);
  const { selectedMainBuyer } = useAppSelector((state) => state.buyers);
  const { selectedInvoices, selectedCreditMemos } = useAppSelector((state) => state.invoice);
  const { buyerUserCanPay } = useBuyerUserPermissions();
  const [selectedTabId, setSelectedTabId] = useState<InvoiceStatus>('OPEN');
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [selectedInvoiceFilterId, setSelectedInvoiceFilterId] = useState<
    'SHOW_OPEN' | 'SHOW_PAST_DUE' | null
  >(null);

  const [searchText, setSearchText] = useState('');
  const [payInvoicesModalOpen, setPayInvoicesModalOpen] = useState(false);
  const [invoicesToBePaid, setInvoicesToBePaid] = useState([] as QuickbooksInvoice[]);
  const [invoiceForPreview, setInvoiceForPreview] = useState<QuickbooksInvoice | null | undefined>(
    null
  );
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [CreditMemoForPreview, setCreditMemoForPreview] = useState<
    QuickbooksCreditMemo | null | undefined
  >(null);
  const [isSinglePay, setIsSinglePay] = useState(false);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const didAutoSelectFromUrlRef = useRef(false);

  const { isPreviewMode, previewMainBuyer } = useAppSelector((state) => state.preview);

  const smallDeviceWidth = 800;

  const invoicesFilterOptions: { name: string; id: 'SHOW_OPEN' | 'SHOW_PAST_DUE' }[] = useMemo(
    () => [
      { name: 'Show all open invoices', id: 'SHOW_OPEN' },
      { name: 'Show past due invoices', id: 'SHOW_PAST_DUE' },
    ],
    []
  );

  useEffect(() => {
    // dispatch(resetSelectInvoice());
  }, [selectedTabId]);

  // Read jobIds from URL when navigating via "Pay by Project"
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const jobIdsParam = searchParams.get('jobIds');
    if (jobIdsParam) {
      const ids = jobIdsParam.split(',').filter(Boolean);
      if (ids.length > 0) {
        didAutoSelectFromUrlRef.current = false;
        setSelectedJobIds(ids);
      }
    }
  }, [location.search]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers && !isPreviewMode) {
      const mainBuyer = user?.userMainBuyers
        .filter((mb) => !mb?.isDeactivated && !mb?.isCODCustomer)
        .find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
      setIsSinglePay(false);
    }
  }, [user?.userMainBuyers, isPreviewMode]);

  useEffect(() => {
    didAutoSelectFromUrlRef.current = false;
    if (selectedMainBuyerId) {
      dispatch(getUserByBuyers({ buyerId: selectedMainBuyerId }));
    }
    dispatch(resetInvoiceIdsForDownload());
    dispatch(resetSelectInvoice());
  }, [selectedMainBuyerId]);

  useEffect(() => {
    if (selectedMainBuyerId && !buyersLoadingList.includes('getGroupedBuyersSuppliers')) {
      dispatch(
        getGroupedBuyersSuppliers({
          mainBuyerId: selectedMainBuyerId,
          weeksLimit: buyerSupplierInvoicesDataWeeksLimit,
        })
      );
    }

    return () => {};
  }, [buyerSupplierInvoicesDataWeeksLimit, selectedMainBuyerId]);

  useEffect(() => {
    let newSupplier: SupplierForBuyer | null = null;

    if (params.supplierDomainId) {
      const supplierDomainId = params.supplierDomainId;

      const supplier: SupplierForBuyer | undefined = selectedBuyerSuppliers.find(
        (item: Supplier) => item.domainId === supplierDomainId
      );

      if (supplier) {
        newSupplier = supplier;
      } else {
        newSupplier = null;
      }
    }
    dispatch(updateSelectedSupplierForBuyer(newSupplier));
  }, [params.supplierDomainId, selectedMainBuyerId, selectedBuyerSuppliers]);

  const selectedSupplier = useMemo(() => {
    let newSupplier: SupplierForBuyer | null = null;

    if (params.supplierDomainId) {
      const supplierDomainId = params.supplierDomainId;

      const supplier: SupplierForBuyer | undefined = selectedBuyerSuppliers.find(
        (item: Supplier) => item.domainId === supplierDomainId
      );

      if (supplier) {
        newSupplier = supplier;
      } else {
        newSupplier = null;
      }
    }
    return newSupplier;
  }, [location.pathname, selectedBuyerSuppliers]);

  // Open scheduled payments for this supplier, so invoices already committed to one can be badged
  // and kept out of a second selection.
  useEffect(() => {
    if (selectedMainBuyerId && selectedSupplier?.id) {
      dispatch(
        getScheduledPayments({
          mainBuyerId: selectedMainBuyerId,
          supplierId: selectedSupplier.id,
        })
      );
    }
  }, [selectedMainBuyerId, selectedSupplier?.id]);

  const selectedBuyer = useMemo(() => {
    if (selectedSupplier) {
      return selectedSupplier.buyer;
    }
    return null;
  }, [selectedSupplier]);

  // Determine if using custom aging ranges
  const isUsingAgingRanges = useMemo(
    () =>
      (selectedSupplier?.isUseAgingRanges &&
        selectedSupplier?.agingRanges &&
        selectedSupplier.agingRanges.length > 0) ??
      false,
    [selectedSupplier?.isUseAgingRanges, selectedSupplier?.agingRanges]
  );

  const summaryDetails = useMemo(() => {
    const documentsSummary = selectedBuyer?.documentsSummary;
    if (selectedSupplier?.invoices && documentsSummary) {
      const activeJobIds = selectedJobIds.filter((id) => id !== 'ALL');
      const isJobFiltered = activeJobIds.length > 0;

      // Get all open invoices for aging bucket calculation, filtered by job if active
      const allInvoices = [
        ...(selectedSupplier?.invoices?.pastDueInvoices.invoices || []),
        ...(selectedSupplier?.invoices?.currentInvoices.invoices || []),
      ];
      const allOpenInvoices = isJobFiltered
        ? allInvoices.filter(
            (inv) => inv.quickBooksJob && activeJobIds.includes(inv.quickBooksJob.id)
          )
        : allInvoices;

      const todayStr = moment().format('YYYY-MM-DD');
      const getDueDateStr = (dueDate: string | Date) =>
        moment.parseZone(dueDate as string).format('YYYY-MM-DD');
      const getDaysPastDue = (dueDate: string | Date) =>
        moment(todayStr).diff(moment(getDueDateStr(dueDate)), 'days');

      // Calculate CURRENT DUE invoices based on actual due dates for click functionality
      const currentDueInvoices = allOpenInvoices.filter((inv) => {
        if (!inv.dueDate) return false;
        return getDueDateStr(inv.dueDate) >= todayStr; // Due today or in the future
      });

      const allCreditMemos = selectedSupplier?.invoices?.creditMemosDetails?.creditMemos || [];
      const openCreditMemos = isJobFiltered
        ? allCreditMemos.filter(
            (cm) => cm.quickBooksJob && activeJobIds.includes(cm.quickBooksJob.id)
          )
        : allCreditMemos;

      // Sum of remaining credit for jobs that match the current job filter
      const jobCreditTotal = isJobFiltered
        ? openCreditMemos.reduce((sum, cm) => sum + (cm.remainingCredit ?? 0), 0)
        : 0;

      // === Pre-compute gross bucket amounts and apply credits oldest-first ===

      type BucketEntry = {
        order: number;
        invoices: (QuickbooksInvoice | QuickbooksCreditMemo)[];
        creditMemos: QuickbooksCreditMemo[];
        adjustedAmount: number;
      };

      const sortedRanges = isUsingAgingRanges
        ? [
            ...(selectedSupplier?.agingRanges && selectedSupplier.agingRanges.length > 0
              ? selectedSupplier.agingRanges
              : DEFAULT_AGING_RANGES),
          ].sort((a, b) => a.order - b.order)
        : null;

      const computedBuckets: BucketEntry[] = [];

      if (isUsingAgingRanges && sortedRanges) {
        for (const range of sortedRanges) {
          const bucketInvoices = allOpenInvoices.filter((inv) => {
            if (!inv.dueDate) return false;
            const daysPastDue = getDaysPastDue(inv.dueDate);
            if (daysPastDue <= 0) return false;
            return calculateAgingBucket(daysPastDue, sortedRanges) === range.order;
          });
          computedBuckets.push({
            order: range.order,
            invoices: bucketInvoices,
            creditMemos: [],
            adjustedAmount: bucketInvoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0),
          });
        }
      } else {
        const b0: (QuickbooksInvoice | QuickbooksCreditMemo)[] = [];
        const b1: (QuickbooksInvoice | QuickbooksCreditMemo)[] = [];
        const b2: (QuickbooksInvoice | QuickbooksCreditMemo)[] = [];
        const b3: (QuickbooksInvoice | QuickbooksCreditMemo)[] = [];
        for (const inv of allOpenInvoices) {
          if (!inv.dueDate) continue;
          const daysPastDue = getDaysPastDue(inv.dueDate);
          if (daysPastDue <= 0) continue;
          if (daysPastDue <= 30) b0.push(inv);
          else if (daysPastDue <= 60) b1.push(inv);
          else if (daysPastDue <= 90) b2.push(inv);
          else b3.push(inv);
        }
        const toAmount = (arr: (QuickbooksInvoice | QuickbooksCreditMemo)[]) =>
          arr.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
        computedBuckets.push(
          { order: 0, invoices: b0, creditMemos: [], adjustedAmount: toAmount(b0) },
          { order: 1, invoices: b1, creditMemos: [], adjustedAmount: toAmount(b1) },
          { order: 2, invoices: b2, creditMemos: [], adjustedAmount: toAmount(b2) },
          { order: 3, invoices: b3, creditMemos: [], adjustedAmount: toAmount(b3) }
        );
      }

      // Attribute each credit to one bucket so clicking an aging category also selects the
      // credits behind that category's amount. Runs off the gross amounts, before the
      // job-filtered adjustment below consumes them.
      const creditAssignment = assignCreditsToAgingBuckets(
        openCreditMemos,
        computedBuckets.map((bucket) => ({ order: bucket.order, amount: bucket.adjustedAmount })),
        sortedRanges ?? DEFAULT_AGING_RANGES,
        selectedSupplier?.arSettings?.agedCreditMemos === true
      );
      computedBuckets.forEach((bucket) => {
        bucket.creditMemos = creditAssignment.byBucketOrder[bucket.order] ?? [];
      });

      // Apply credits oldest-first (descending order) to adjust bucket amounts
      let adjustedCurrentDue = currentDueInvoices.reduce((sum, inv) => sum + (inv.balance ?? 0), 0);
      if (isJobFiltered && jobCreditTotal > 0) {
        let remaining = jobCreditTotal;
        const descBuckets = [...computedBuckets].sort((a, b) => b.order - a.order);
        for (const bucket of descBuckets) {
          if (remaining <= 0) break;
          const reduce = Math.min(remaining, Math.max(0, bucket.adjustedAmount));
          bucket.adjustedAmount -= reduce;
          remaining -= reduce;
        }
        adjustedCurrentDue = adjustedCurrentDue - remaining;
      }

      const totalAmountDue = isJobFiltered
        ? computedBuckets.reduce((sum, b) => sum + b.adjustedAmount, 0) + adjustedCurrentDue
        : (documentsSummary?.totalAmountDueInvoices ?? 0).toString();

      const totalCurrentDue = isJobFiltered
        ? adjustedCurrentDue
        : (documentsSummary?.totalCurrentDueInvoices ?? 0).toString();

      // Base summary items (always present)
      const baseSummary = [
        {
          key: 'summary_1',
          icon: (
            <div className="h-[24px] w-[24px] flex items-center justify-center mr-1 rounded-full bg-gray-50 border-gray-500 text-gray-700">
              <CurrencyCircleDollar size={20} />
            </div>
          ),
          value: formatMoney(totalAmountDue, 2),
          line1: 'AMOUNT',
          line2: 'DUE',
          invoices: [],
        },
        {
          key: 'summary_2',
          icon: (
            <div className="mr-1 h-[24px] w-[24px] flex items-center justify-center rounded-full bg-emerald-50 border-emerald-500 text-emerald-500">
              <Clock size={20} />
            </div>
          ),
          value: formatMoney(totalCurrentDue, 2),
          line1: 'CURRENT',
          line2: 'DUE',
          invoices: [...currentDueInvoices, ...creditAssignment.currentDue],
        },
      ];

      let agingSummary = [];

      if (isUsingAgingRanges && sortedRanges) {
        const bucketColors = [
          { bg: 'bg-[#E8FFFF]', text: 'text-[#0456A2]' },
          { bg: 'WARNING_100-BG', text: 'WARNING_500-CLR' },
          { bg: 'bg-[#FAEFFF]', text: 'text-[#8C04A2]' },
          { bg: 'bg-[#FFE8E8]', text: 'text-[#A20404]' },
        ];

        agingSummary = sortedRanges.map((range, index) => {
          const colorIndex = index % bucketColors.length;
          const colors = bucketColors[colorIndex];
          const displayLabel = range.label.toUpperCase();

          const bucketEntry = computedBuckets.find((b) => b.order === range.order);
          const serverBucket = documentsSummary.agingBuckets?.find((b) => b.order === range.order);
          const amount = isJobFiltered
            ? bucketEntry?.adjustedAmount ?? 0
            : serverBucket?.amount ?? 0;

          return {
            key: `summary_aging_${range.order}`,
            icon: (
              <div
                className={`mr-2 ${colors.bg} ${colors.text} p-0.5 sm:p-1 rounded-full font-semibold text-2xs sm:text-xs`}>
                {range.max === null ? `>${range.min - 1}` : range.max}
              </div>
            ),
            value: formatMoney(amount, 2),
            line1: `${displayLabel} DAYS`,
            line2: 'PAST DUE',
            invoices: [...(bucketEntry?.invoices ?? []), ...(bucketEntry?.creditMemos ?? [])],
          };
        });
      } else {
        const [b0, b1, b2, b3] = computedBuckets;
        agingSummary = [
          {
            key: 'summary_3',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-red-50 border bg-amber-50 border-amber-500 text-amber-500 p-1 rounded-full font-semibold text-[10px]">
                30
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b0.adjustedAmount
                : (documentsSummary?.totalPastDue1To30Invoices ?? 0).toString(),
              2
            ),
            line1: '1 - 30 DAYS',
            line2: 'PAST DUE',
            invoices: [...b0.invoices, ...b0.creditMemos],
          },
          {
            key: 'summary_4',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-red-50 border bg-orange-50 border-orange-500 text-orange-500 p-1 rounded-full font-semibold text-[10px]">
                60
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b1.adjustedAmount
                : (documentsSummary?.totalPastDue31To60Invoices ?? 0).toString(),
              2
            ),
            line1: '31 - 60 DAYS',
            line2: 'PAST DUE',
            invoices: [...b1.invoices, ...b1.creditMemos],
          },
          {
            key: 'summary_5',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-red-50 border border-red-500 text-red-500 p-1 rounded-full font-semibold text-[10px]">
                90
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b2.adjustedAmount
                : (documentsSummary?.totalPastDue61To90Invoices ?? 0).toString(),
              2
            ),
            line1: '61 - 90 DAYS',
            line2: 'PAST DUE',
            invoices: [...b2.invoices, ...b2.creditMemos],
          },
          {
            key: 'summary_6',
            icon: (
              <div className="mr-2 flex items-center justify-center h-[24px] w-[24px] bg-rose-50 border border-rose-600 text-rose-600 p-1 rounded-full font-semibold text-[10px]">
                {`>90`}
              </div>
            ),
            value: formatMoney(
              isJobFiltered
                ? b3.adjustedAmount
                : (documentsSummary?.totalPastDueOver90Invoices ?? 0).toString(),
              2
            ),
            line1: 'OVER 90 DAYS',
            line2: 'PAST DUE',
            invoices: [...b3.invoices, ...b3.creditMemos],
          },
        ];
      }

      return [...baseSummary, ...agingSummary];
    }
    return [];
  }, [selectedSupplier, selectedBuyer, isUsingAgingRanges, selectedJobIds]);

  const invoicesTabs = useMemo(() => {
    const allOpenInvoices = [
      ...(selectedSupplier?.invoices?.pastDueInvoices.invoices || []),
      ...(selectedSupplier?.invoices?.currentInvoices.invoices || []),
      ...(selectedSupplier?.invoices?.creditMemosDetails.creditMemos || []),
    ];
    let totalPendingCredits = 0;
    if (selectedSupplier?.invoices?.creditMemosDetails.pendingCreditMemos) {
      totalPendingCredits =
        selectedSupplier?.invoices?.creditMemosDetails.pendingCreditMemos.reduce((prev, curr) => {
          return prev + curr.balance;
        }, 0);
    }

    const pp: InvoiceTabs[] = [
      {
        id: 'OPEN',
        name: 'Open Items',
        totalAmount:
          (selectedSupplier?.invoices?.pastDueInvoices?.totalAmount || 0) +
          (selectedSupplier?.invoices?.currentInvoices?.totalAmount || 0) -
          (selectedSupplier?.invoices?.pendingPayInvoices?.totalAmount || 0) +
          totalPendingCredits,
        invoices: [...allOpenInvoices],
        credits: selectedSupplier?.invoices?.creditMemosDetails.creditMemos || [],
      },
      {
        id: 'PENDING_PAY',
        name: 'Pending',
        totalAmount:
          (selectedSupplier?.invoices?.pendingPayInvoices?.totalAmount || 0) - totalPendingCredits,
        invoices: [
          ...(selectedSupplier?.invoices?.pendingPayInvoices.invoices || []),
          ...(selectedSupplier?.invoices?.creditMemosDetails.pendingCreditMemos || []),
        ],
        credits: selectedSupplier?.invoices?.creditMemosDetails.pendingCreditMemos || [],
      },
      {
        id: 'PAID',
        name: 'Paid',
        totalAmount: selectedSupplier?.invoices?.paidInvoices?.totalAmount || 0,
        invoices: selectedSupplier?.invoices?.paidInvoices.invoices || [],
        credits: [],
      },
    ];
    if (
      allOpenInvoices.length === 0 &&
      selectedSupplier?.invoices &&
      selectedSupplier?.invoices?.pendingPayInvoices.invoices.length > 0
    ) {
      setSelectedTabId('PENDING_PAY');
    }
    return pp;
  }, [selectedSupplier, location.pathname]);

  // Auto-select open invoices for jobs passed via ?jobIds= URL param (Pay by Project flow).
  // Depends on selectedMainBuyerId so it re-runs after the ID resolves from null→value,
  // which happens after the resetSelectInvoice dispatch in the [selectedMainBuyerId] effect.
  useEffect(() => {
    if (didAutoSelectFromUrlRef.current || !selectedMainBuyerId) return;
    const searchParams = new URLSearchParams(location.search);
    const jobIdsParam = searchParams.get('jobIds');
    if (!jobIdsParam || !selectedSupplier?.invoices) return;

    const ids = jobIdsParam.split(',').filter(Boolean);
    if (ids.length === 0) return;

    const matchingInvoices = [
      ...(selectedSupplier.invoices.pastDueInvoices?.invoices || []),
      ...(selectedSupplier.invoices.currentInvoices?.invoices || []),
    ].filter((inv) => inv.quickBooksJob && ids.includes(inv.quickBooksJob.id));

    const matchingCredits = (
      selectedSupplier.invoices.creditMemosDetails?.creditMemos || []
    ).filter((cm) => cm.quickBooksJob && ids.includes(cm.quickBooksJob.id));

    if (matchingInvoices.length > 0 || matchingCredits.length > 0) {
      didAutoSelectFromUrlRef.current = true;
      if (matchingInvoices.length > 0) {
        dispatch(selectInvoice({ tabId: 'OPEN', invoices: matchingInvoices }));
        // invoiceIdsForDownload drives the checkbox checked state in the table
        dispatch(
          selectInvoiceIdsForDownload({
            tabId: 'OPEN',
            invoiceIds: matchingInvoices.map((inv) => inv.id),
          })
        );
      }
      if (matchingCredits.length > 0) {
        dispatch(selectCreditMemo({ tabId: 'OPEN', creditMemos: matchingCredits }));
      }
    }
  }, [selectedSupplier, selectedMainBuyerId, location.search]);

  const tableInvoices: (QuickbooksInvoice | QuickbooksCreditMemo)[] = useMemo(() => {
    if (selectedSupplier && selectedTabId) {
      const tab = invoicesTabs.find((tabDetails) => tabDetails.id === selectedTabId);

      logEvent(myAnalytics, 'buyer_view_tab', {
        userId: user ? user.id : '',
        supplierId: selectedSupplier.id,
        status: selectedTabId.toLocaleLowerCase().replaceAll('_', ' '),
        time: new Date(),
        route: location.pathname,
      });
      if (tab) {
        return [...tab.invoices];
      } else {
        return [];
      }
    }

    return [];
  }, [invoicesTabs, selectedTabId]);
  const selectedSupplierJobs = useMemo(() => {
    if (tableInvoices) {
      const jobs = invoicesTabs
        .reduce((prev: (QuickbooksInvoice | QuickbooksCreditMemo)[], curr) => {
          return [...prev, ...curr.invoices];
        }, [])
        .reduce((prev: QuickbooksJob[], curr) => {
          if (curr.jobId && curr.quickBooksJob) {
            return [...prev, curr.quickBooksJob];
          } else {
            return prev;
          }
        }, []);

      return removeDuplicatesByProperty({ inputArray: jobs, property: 'id' });
    }

    return tableInvoices;
  }, [invoicesTabs, selectedTabId]);

  const activeJobIds = useMemo(() => selectedJobIds.filter((id) => id !== 'ALL'), [selectedJobIds]);

  const activeJobs = useMemo(
    () => (selectedSupplierJobs as QuickbooksJob[]).filter((j) => activeJobIds.includes(j.id)),
    [selectedSupplierJobs, activeJobIds]
  );

  const searchedTableInvoices = useMemo(() => {
    let finalSearchedInvoices = [];
    if (tableInvoices) {
      const searched = tableInvoices.filter((documentDetails) => {
        const invoiceCheck = QuickbooksInvoiceSchema.safeParse(documentDetails);
        const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(documentDetails);
        let invoice: QuickbooksInvoice | null = null;
        let creditMemo: QuickbooksCreditMemo | null = null;
        if (invoiceCheck.success) {
          invoice = invoiceCheck.data;
        }
        if (creditMemoCheck.success) {
          creditMemo = creditMemoCheck.data;
        }
        let filters: {
          isMatchTextFilter?: boolean;
          isMatchJobFilter?: boolean;
          isMatchInvoiceFilter?: boolean;
        } = {};
        if (searchText) {
          if (
            (invoice &&
              ((invoice?.quickBooksJob?.jobName &&
                invoice?.quickBooksJob?.jobName
                  .toString()
                  .toLowerCase()
                  .includes(searchText.toLowerCase())) ||
                (invoice?.quickBooksJob?.address &&
                  invoice?.quickBooksJob?.address
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.quickBooksJob?.jobNumber &&
                  invoice?.quickBooksJob?.jobNumber
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.poNumber &&
                  invoice?.poNumber.toString().toLowerCase().includes(searchText.toLowerCase())) ||
                (invoice?.amount &&
                  Number(invoice?.amount)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.terms &&
                  invoice?.terms.toString().toLowerCase().includes(searchText.toLowerCase())) ||
                (invoice?.dueDate &&
                  moment(invoice?.dueDate)
                    .format('MM/DD/YYYY')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.createdAt &&
                  moment(invoice?.createdAt)
                    .format('MM/DD/YYYY')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.docNumber &&
                  invoice?.docNumber.toString().toLowerCase().includes(searchText.toLowerCase())) ||
                (invoice?.balance &&
                  Number(invoice?.balance)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (invoice?.recordType &&
                  invoice?.recordType
                    .toString()
                    .toLowerCase()
                    .replace('_', ' ')
                    .includes(searchText.toLowerCase())))) ||
            (creditMemo &&
              ((creditMemo?.quickBooksJob?.jobName &&
                creditMemo?.quickBooksJob?.jobName
                  .toString()
                  .toLowerCase()
                  .includes(searchText.toLowerCase())) ||
                (creditMemo?.quickBooksJob?.address &&
                  creditMemo?.quickBooksJob?.address
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.quickBooksJob?.jobNumber &&
                  creditMemo?.quickBooksJob?.jobNumber
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.docNumber &&
                  creditMemo?.docNumber
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.totalAmount &&
                  Number(creditMemo?.totalAmount)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.jobName &&
                  creditMemo?.jobName
                    .toString()
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.createdAt &&
                  moment(creditMemo?.createdAt)
                    .format('MM/DD/YYYY')
                    .toLowerCase()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.remainingCredit &&
                  Number(creditMemo?.remainingCredit)
                    .toFixed(2)
                    .toString()
                    .includes(searchText.toLowerCase())) ||
                (creditMemo?.recordType &&
                  creditMemo?.recordType
                    .toString()
                    .toLowerCase()
                    .replace('_', ' ')
                    .includes(searchText.toLowerCase()))))
          ) {
            filters = { ...filters, isMatchTextFilter: true };
          } else {
            filters = { ...filters, isMatchTextFilter: false };
          }
        }
        if (selectedJobIds.length > 0) {
          if (invoice && invoice?.quickBooksJob) {
            filters = {
              ...filters,
              isMatchJobFilter:
                invoice?.quickBooksJob && selectedJobIds?.includes(invoice.quickBooksJob.id),
            };
          } else if (creditMemo && creditMemo?.quickBooksJob) {
            filters = {
              ...filters,
              isMatchJobFilter:
                creditMemo?.quickBooksJob && selectedJobIds?.includes(creditMemo.quickBooksJob.id),
            };
          } else {
            filters = {
              ...filters,
              isMatchJobFilter: false,
            };
          }
        }
        if (selectedInvoiceFilterId) {
          if (
            invoice &&
            selectedInvoiceFilterId === 'SHOW_OPEN' &&
            (invoice.status === 'PAST_DUE' || invoice.status === 'CURRENT_DUE')
          ) {
            filters = { ...filters, isMatchInvoiceFilter: true };
          } else if (
            invoice &&
            selectedInvoiceFilterId === 'SHOW_PAST_DUE' &&
            invoice.status === 'PAST_DUE'
          ) {
            filters = { ...filters, isMatchInvoiceFilter: true };
          } else {
            filters = { ...filters, isMatchInvoiceFilter: false };
          }
        }

        return Object.values(filters).every((item) => item);
      });
      finalSearchedInvoices = searched;
    } else {
      finalSearchedInvoices = tableInvoices;
    }

    return finalSearchedInvoices;
  }, [tableInvoices, searchText, selectedJobIds, selectedInvoiceFilterId]);

  const statementPdfsOptions = useMemo(() => {
    const mainBuyer = isPreviewMode
      ? previewMainBuyer
      : (user?.userMainBuyers || []).find((item) => item.id === selectedMainBuyerId);
    if (!mainBuyer) return [];
    const buyerSupplier = selectedBuyerSuppliers.find(
      (item) => item.buyer.id === mainBuyer?.buyerId
    );
    if (!buyerSupplier) return [];
    const buyer = buyerSupplier.buyer;
    if (buyer?.historicalStatements && buyer.historicalStatements.length > 0) {
      return [{ name: 'Today', url: 'TODAY' }, ...buyer.historicalStatements].map((item) => ({
        label: `Statement ${
          item.name && item.url !== 'TODAY'
            ? item.name.split('__')[0]
            : item.url === 'TODAY'
            ? item.name || ''
            : ''
        }`,
        value: item.url || '',
        extraData: item.name || '',
      }));
    }
    return [];
  }, [selectedMainBuyerId, selectedBuyerSuppliers, isPreviewMode, previewMainBuyer, user]);

  const handleDownloadInvoiceStatements = async () => {
    const allOpenDocuments = [
      ...(selectedSupplier?.invoices?.pastDueInvoices.invoices || []),
      ...(selectedSupplier?.invoices?.currentInvoices.invoices || []),
      ...(selectedSupplier?.invoices?.creditMemosDetails.creditMemos || []),
      ...(selectedSupplier?.invoices?.creditMemosDetails.pendingCreditMemos || []),
    ];
    const filteredInvoiceIds =
      activeJobIds.length > 0
        ? allOpenDocuments
            .filter((doc) => doc.quickBooksJob && activeJobIds.includes(doc.quickBooksJob.id))
            .map((doc) => doc.id)
        : undefined;
    const res = await dispatch(
      mainBuyerDownloadSupplierInvoiceStatements({
        mainBuyerId: selectedMainBuyerId || '',
        supplierDomainId: selectedSupplier?.domainId || '',
        ...(activeJobIds.length > 0 ? { jobIds: activeJobIds } : {}),
        ...(filteredInvoiceIds ? { invoiceIds: filteredInvoiceIds } : {}),
      })
    );
    if (res && res.type === 'invoice/mainBuyerDownloadSupplierInvoiceStatements/fulfilled') {
      openDillToast({ type: 'SUCCESS', message: 'Invoice statement download success' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Invoice statement download failed' });
    }
  };

  const handleDownloadAll = async ({ combined }: { combined: boolean }) => {
    if (Object.values(invoiceIdsForDownload).flat().length > 0 && selectedSupplier?.domainId) {
      if (Object.values(invoiceIdsForDownload).flat().length === 1) {
        const res = await dispatch(
          getInvoicePdf({
            supplierDomainId: selectedSupplier?.domainId,
            invoiceId: Object.values(invoiceIdsForDownload).flat()[0],
          })
        );
        if (res && res.type === 'buyers/getInvoicePdf/fulfilled') {
          openDillToast({ message: 'invoice pdf successfully downloaded', type: 'SUCCESS' });
        } else {
          openDillToast({ message: 'Failed to download invoice pdf', type: 'ERROR' });
        }
      } else {
        const res = await dispatch(
          downloadMultiInvoicePdfZip({
            supplierDomainId: selectedSupplier?.domainId || '',
            invoiceIds: Object.values(invoiceIdsForDownload).flat(),
            combined,
          })
        );
        if (res && res.type === 'buyers/downloadMultiInvoicePdfZip/fulfilled') {
          openDillToast({ message: 'invoice pdfs successfully downloaded', type: 'SUCCESS' });
        } else {
          openDillToast({ message: 'Failed to download invoice pdfs', type: 'ERROR' });
        }
      }
    }
  };

  const handlePayBulkInvoice = async () => {
    setIsSinglePay(false);
    await setInvoicesToBePaid(Object.values(selectedInvoices).flat());
    setPayInvoicesModalOpen(true);
  };

  const handlePaySingleInvoice = async (invoice: QuickbooksInvoice) => {
    setIsSinglePay(true);
    await setInvoicesToBePaid([invoice]);
    setPayInvoicesModalOpen(true);
  };

  const handleViewDocument = (doc: QuickbooksInvoice | QuickbooksCreditMemo) => {
    const invoiceCheck = QuickbooksInvoiceSchema.safeParse(doc);
    const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(doc);
    if (invoiceCheck.success) {
      setInvoiceForPreview(invoiceCheck.data);
    } else if (creditMemoCheck.success) {
      setCreditMemoForPreview(creditMemoCheck.data);
    }
  };

  const getData = (supplierId: string) => {
    const lastSyncTimeRef = ref(myRealtimeDB, `lastSyncTime/${supplierId}`);
    const subScription = onValue(lastSyncTimeRef, (snapshot) => {
      const data = snapshot.val();
      dispatch(updateLastSyncTimeBuyer(data));
    });
    return subScription;
  };

  useEffect(() => {
    let subscription: Unsubscribe | null = null;
    if (selectedSupplier) {
      subscription = getData(selectedSupplier.id);
    }
    return () => {
      if (subscription) {
        subscription();
      }
    };
  }, [selectedSupplier]);

  useEffect(() => {
    if (
      selectedSupplier &&
      location.pathname.startsWith(
        `/companies/${selectedMainBuyerId}/invoices/${selectedSupplier.domainId}`
      )
    ) {
      const searchParams = new URLSearchParams(location.search);
      const invoiceId = searchParams.get('invoiceId');
      const creditMemoId = searchParams.get('creditMemoId');
      if (invoiceId) {
        let tabIdWithInvoice: InvoiceStatus | null = null;
        let selectedInvoice: QuickbooksInvoice | null = null;
        invoicesTabs.forEach((tabData) => {
          const invoice = tabData.invoices.find(
            (invoiceDetails) => invoiceDetails.id === invoiceId
          );
          const invoiceCheck = QuickbooksInvoiceSchema.safeParse(invoice);
          if (invoiceCheck.success && invoice) {
            tabIdWithInvoice = tabData.id;
            selectedInvoice = invoiceCheck.data;
          }
        });

        if (selectedInvoice && tabIdWithInvoice) {
          setInvoiceForPreview(selectedInvoice);
          setSelectedTabId(tabIdWithInvoice);
        }
      }
      if (creditMemoId) {
        let tabIdWithInvoice: InvoiceStatus | null = null;
        let selectedCreditMemo: QuickbooksCreditMemo | null = null;
        invoicesTabs.forEach((tabData) => {
          const creditMemo = tabData.credits.find(
            (creditDetails) => creditDetails.id === creditMemoId
          );
          const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(creditMemo);
          if (creditMemoCheck.success && creditMemo) {
            tabIdWithInvoice = tabData.id;
            selectedCreditMemo = creditMemoCheck.data;
          }
        });

        if (selectedCreditMemo && tabIdWithInvoice) {
          setCreditMemoForPreview(selectedCreditMemo);
          setSelectedTabId(tabIdWithInvoice);
        }
      }
    }
  }, [location.pathname, invoicesTabs, selectedSupplier, selectedTabId]);

  const accumulateAmount = useMemo(() => {
    const flatInvoices = Object.values(selectedInvoices).flat();
    if (flatInvoices.length > 0) {
      const flatCreditMemos = Object.values(selectedCreditMemos).flat();
      const totalBalance = flatInvoices.reduce((acc, invoice) => acc + (invoice.balance ?? 0), 0);
      const creditAmount = flatCreditMemos.reduce(
        (acc, creditMemo) => acc + (creditMemo.balance ?? 0),
        0
      );
      return totalBalance - creditAmount;
    }
    return 0;
  }, [selectedInvoices, selectedCreditMemos]);

  return (
    <div className="w-full h-full">
      {width <= smallDeviceWidth && (
        <AppViewInvoiceWithoutPdfModal
          open={!!invoiceForPreview}
          handleClose={() => setInvoiceForPreview(null)}
          selectedInvoice={invoiceForPreview}
          selectedSupplier={selectedSupplier as Supplier}
        />
      )}
      {payInvoicesModalOpen && (
        <AppSelectedPayInvoicesModal
          open={payInvoicesModalOpen}
          title="Bulk Pay"
          handleClose={() => {
            setInvoicesToBePaid([]);
            setPayInvoicesModalOpen(false);
          }}
          selectedInvoices={invoicesToBePaid}
          paymentTo={selectedSupplier?.name?.toLocaleLowerCase()}
          paymentFrom={selectedMainBuyer?.name.toLocaleLowerCase()}
          selectedCreditMemos={isSinglePay ? [] : Object.values(selectedCreditMemos).flat()}
        />
      )}
      {width <= smallDeviceWidth ? (
        <ViewSupplierInvoicesMobile
          invoicesTabs={invoicesTabs}
          selectedTabId={selectedTabId}
          onTabChange={(tabId) => {
            const searchParams = new URLSearchParams(location.search);
            const invoiceId = searchParams.get('invoiceId');
            if (invoiceId) navigate(location.pathname);
            setSelectedTabId(tabId);
          }}
          searchText={searchText}
          onSearchChange={setSearchText}
          filteredInvoices={searchedTableInvoices}
          summaryDetails={summaryDetails}
          onPaySingle={handlePaySingleInvoice}
          onBulkPay={handlePayBulkInvoice}
          onBulkDownload={(combined) => handleDownloadAll({ combined })}
          onView={handleViewDocument}
          buyerUserCanPay={buyerUserCanPay}
          isSupplierVerified={selectedSupplier?.isFullyVerified ?? false}
          accumulateAmount={accumulateAmount}
          selectedSupplierJobs={selectedSupplierJobs as QuickbooksJob[]}
          selectedJobIds={selectedJobIds}
          onJobIdsChange={(ids) => {
            dispatch(resetInvoiceIdsForDownload());
            dispatch(resetSelectInvoice());
            setSelectedJobIds(ids);
          }}
          selectedInvoiceFilterId={selectedInvoiceFilterId}
          onInvoiceFilterChange={setSelectedInvoiceFilterId}
          selectedMainBuyerId={selectedMainBuyerId}
          supplierId={selectedSupplier?.id ?? ''}
          supplierDomainId={selectedSupplier?.domainId ?? ''}
          buyerSupplierInvoicesDataWeeksLimit={buyerSupplierInvoicesDataWeeksLimit}
          onWeeksLimitChange={(weeks) => dispatch(updateBuyerSupplierInvoicesDataWeeksLimit(weeks))}
          statementPdfsOptions={statementPdfsOptions}
          onDownloadStatement={handleDownloadInvoiceStatements}
        />
      ) : (
        <div className="flex h-full flex-col px-2 sm:px-4 flex-nowrap relative overflow-y-scroll">
          <div className="flex">
            {/* Jobs Filter */}
            {selectedSupplierJobs.length > 0 && (
              <div className="flex items-center gap-2 mt-2 flex-wrap w-full">
                <div className="relative z-20">
                  <DillDropdown
                    inputFieldStyles={{ width: '160px', height: '36px', padding: '2px 2px' }}
                    placeholder="Jobs"
                    isSearchable={false}
                    isCreatable={false}
                    closeMenu={false}
                    customViewElement={
                      <div className="flex my-1 px-2 items-center">
                        <Buildings size={16} className="text-blue-500" />
                        <p className="text-sm ml-2 text-gray-600">Jobs Filter</p>
                      </div>
                    }
                    itemWidth={300}
                    items={[{ jobName: 'All Jobs', id: 'ALL' }, ...selectedSupplierJobs].map(
                      (job) => {
                        const selectedJob = (selectedSupplierJobs as QuickbooksJob[]).find(
                          (j) => j.id === job.id
                        );
                        const jobAddress = `${selectedJob?.address ?? ''} ${
                          selectedJob?.city ?? ''
                        } ${selectedJob?.state ? `${selectedJob?.state},` : ''} ${
                          selectedJob?.zipCode ?? ''
                        }`.trim();
                        const jobName = `${
                          selectedJob?.jobNumber ? `${selectedJob?.jobNumber} - ` : ''
                        }${job.jobName}`;
                        return {
                          label: jobName,
                          value: job.id,
                          labelInElement: (
                            <div className="flex m-1 items-center gap-1">
                              <DillCheckbox isChecked={selectedJobIds?.includes(job.id)} />
                              <Buildings size={16} className="text-blue-500 flex-shrink-0" />
                              <div className="flex-1 truncate">
                                <div className="text-black text-xs truncate">{jobName}</div>
                                {jobAddress !== '' && (
                                  <div className="text-gray-500 text-2xs truncate">
                                    {jobAddress}
                                  </div>
                                )}
                              </div>
                            </div>
                          ),
                        };
                      }
                    )}
                    isMulti
                    onMultiSelectChange={(data, oldMultiValue) => {
                      dispatch(resetInvoiceIdsForDownload());
                      dispatch(resetSelectInvoice());
                      if (data) {
                        const valueList = data.map((item) => item.value);
                        const valueListWithoutAll = valueList.filter((item) => item !== 'ALL');
                        if (
                          !valueList.includes('ALL') &&
                          oldMultiValue &&
                          oldMultiValue.includes('ALL') &&
                          valueListWithoutAll.length === selectedSupplierJobs.length
                        ) {
                          setSelectedJobIds([]);
                        } else if (
                          valueList.includes('ALL') &&
                          valueListWithoutAll.length < selectedSupplierJobs.length &&
                          oldMultiValue &&
                          oldMultiValue.includes('ALL')
                        ) {
                          setSelectedJobIds(valueListWithoutAll);
                        } else if (
                          (valueList.includes('ALL') &&
                            valueListWithoutAll.length === 0 &&
                            oldMultiValue &&
                            !oldMultiValue.includes('ALL')) ||
                          valueList.includes('ALL') ||
                          (valueListWithoutAll.length === selectedSupplierJobs.length &&
                            !valueList.includes('ALL'))
                        ) {
                          setSelectedJobIds([
                            'ALL',
                            ...selectedSupplierJobs.map((item) => item.id),
                          ]);
                        } else if (valueList.length > 0) {
                          setSelectedJobIds(valueList);
                        } else {
                          setSelectedJobIds([]);
                        }
                      }
                    }}
                    value={null}
                    multiValue={selectedJobIds}
                    menuAlign="left"
                  />
                  {activeJobIds.length > 0 && (
                    <div className="flex absolute bottom-6 right-[-12px] bg-blue-500 text-white rounded-full text-xs font-bold h-5 w-5 items-center justify-center">
                      {activeJobIds.length}
                    </div>
                  )}
                </div>
                {activeJobIds.length > 0 && (
                  <>
                    {activeJobs.slice(0, 2).map((job) => {
                      const jobLabel = `${job.jobNumber ? `${job.jobNumber} - ` : ''}${
                        job.jobName
                      }`;
                      return (
                        <span
                          key={job.id}
                          className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium border border-blue-200 max-w-[200px]">
                          <Buildings size={10} className="flex-shrink-0" />
                          <span className="truncate">{jobLabel}</span>
                          <button
                            className="flex-shrink-0 flex items-center justify-center rounded-full hover:bg-red-200 p-0.5 cursor-pointer bg-transparent border-none text-red-500"
                            onClick={() => {
                              const next = selectedJobIds.filter(
                                (id) => id !== job.id && id !== 'ALL'
                              );
                              dispatch(resetInvoiceIdsForDownload());
                              dispatch(resetSelectInvoice());
                              setSelectedJobIds(next);
                            }}>
                            <X size={10} />
                          </button>
                        </span>
                      );
                    })}
                    {activeJobs.length > 2 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium border border-gray-200">
                        +{activeJobs.length - 2}
                      </span>
                    )}
                    <button
                      className="text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer bg-transparent border-none"
                      onClick={() => {
                        setSelectedJobIds([]);
                        dispatch(resetInvoiceIdsForDownload());
                        dispatch(resetSelectInvoice());
                      }}>
                      Clear All
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="w-full flex items-center justify-end gap-2 pt-2 px-4">
              <AppLastSyncDataView dataKey="invoices" from="buyer" />
            </div>
          </div>
          {/* Aging Details Summary */}
          {summaryDetails && summaryDetails.length > 0 && (
            <div className="mb-2 mt-2">
              {(() => {
                const isPaidTab = selectedTabId === 'PAID' || selectedTabId === 'PENDING_PAY';
                const currentTabDownloadIds = invoiceIdsForDownload[selectedTabId] || [];
                const currentTabInvoicesForCheck = selectedInvoices[selectedTabId] || [];
                const currentTabCreditMemoIds = (selectedCreditMemos[selectedTabId] || []).map(
                  (creditMemo) => creditMemo.id
                );

                // A tile selects the invoices behind its amount plus the credits attributed to it.
                // Credits are only in play on tabs where they can be selected for payment, matching
                // InvoicesTable's own rule.
                const splitSelectableDocs = (
                  docs: (QuickbooksInvoice | QuickbooksCreditMemo)[]
                ) => {
                  const invoices: QuickbooksInvoice[] = [];
                  const creditMemos: QuickbooksCreditMemo[] = [];
                  docs.forEach((doc) => {
                    const invoiceCheck = QuickbooksInvoiceSchema.safeParse(doc);
                    if (invoiceCheck.success) {
                      const isPendingReconciliation =
                        invoiceCheck.data.dillPaymentStatus === 'RECONCILIATION_PENDING';
                      const hasNegativeBalance = (invoiceCheck.data.balance ?? 0) < 0;
                      if (!(isPendingReconciliation && hasNegativeBalance)) {
                        invoices.push(invoiceCheck.data);
                      }
                      return;
                    }
                    const creditMemoCheck = QuickbooksCreditMemoSchema.safeParse(doc);
                    if (
                      creditMemoCheck.success &&
                      !isPaidTab &&
                      isCreditMemoSelectable(
                        creditMemoCheck.data.recordType,
                        selectedSupplier?.integrationType
                      )
                    ) {
                      creditMemos.push(creditMemoCheck.data);
                    }
                  });
                  return { invoices, creditMemos };
                };

                const isDocSelected = (doc: QuickbooksInvoice | QuickbooksCreditMemo) =>
                  isPaidTab
                    ? currentTabDownloadIds.includes(doc.id)
                    : currentTabInvoicesForCheck.some((sel) => sel.id === doc.id) ||
                      currentTabCreditMemoIds.includes(doc.id);

                const hasAnyBucketSelected = summaryDetails.some((summary) => {
                  const { invoices, creditMemos } = splitSelectableDocs(summary.invoices);
                  return [...invoices, ...creditMemos].some(isDocSelected);
                });

                return (
                  <div className="flex flex-col">
                    <div className="flex gap-2 w-full overflow-x-auto pb-1">
                      {summaryDetails.map((summary) => {
                        const hasInvoices = summary.invoices && summary.invoices.length > 0;
                        const { invoices: bucketInvoices, creditMemos: bucketCreditMemos } =
                          splitSelectableDocs(summary.invoices);
                        const selectableDocsInBucket = [...bucketInvoices, ...bucketCreditMemos];
                        const selectedDocsInBucket = selectableDocsInBucket.filter(isDocSelected);
                        const isAllSelected =
                          selectableDocsInBucket.length > 0 &&
                          selectedDocsInBucket.length === selectableDocsInBucket.length;

                        return (
                          <div
                            key={summary.key}
                            className={twMerge(
                              'flex flex-col flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-200 bg-white transition-all',
                              hasInvoices && 'cursor-pointer hover:border-blue-300 hover:shadow-sm',
                              isAllSelected
                                ? 'border-blue-500 border-2 bg-blue-50'
                                : selectedDocsInBucket.length > 0 && 'border-blue-300'
                            )}
                            onClick={() => {
                              if (!hasInvoices) return;

                              const invoiceIds = bucketInvoices.map((inv) => inv.id);

                              if (!isPaidTab) {
                                if (isAllSelected) {
                                  dispatch(
                                    unSelectInvoice({
                                      tabId: selectedTabId,
                                      invoices: bucketInvoices,
                                    })
                                  );
                                  dispatch(
                                    unSelectInvoiceIdsForDownload({
                                      tabId: selectedTabId,
                                      invoiceIds,
                                    })
                                  );
                                  if (bucketCreditMemos.length > 0) {
                                    dispatch(
                                      unSelectCreditMemo({
                                        tabId: selectedTabId,
                                        creditMemos: bucketCreditMemos,
                                      })
                                    );
                                  }
                                } else {
                                  dispatch(
                                    selectInvoice({
                                      tabId: selectedTabId,
                                      invoices: bucketInvoices,
                                    })
                                  );
                                  dispatch(
                                    selectInvoiceIdsForDownload({
                                      tabId: selectedTabId,
                                      invoiceIds,
                                    })
                                  );
                                  if (bucketCreditMemos.length > 0) {
                                    dispatch(
                                      selectCreditMemo({
                                        tabId: selectedTabId,
                                        creditMemos: bucketCreditMemos,
                                      })
                                    );
                                  }
                                  setSelectedRowId(null);
                                }
                              } else {
                                if (isAllSelected) {
                                  dispatch(
                                    unSelectInvoiceIdsForDownload({
                                      tabId: selectedTabId,
                                      invoiceIds,
                                    })
                                  );
                                } else {
                                  dispatch(
                                    selectInvoiceIdsForDownload({
                                      tabId: selectedTabId,
                                      invoiceIds,
                                    })
                                  );
                                  setSelectedRowId(null);
                                }
                              }
                            }}>
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-1">
                                {summary.icon}
                                <span className="font-bold text-xs">{summary.value}</span>
                              </div>
                              {hasInvoices && (
                                <span
                                  className={twMerge(
                                    'text-xs font-semibold',
                                    isAllSelected
                                      ? 'PRIMARY_500-CLR'
                                      : selectedDocsInBucket.length > 0
                                      ? 'text-blue-400'
                                      : 'GREY_400-CLR'
                                  )}>
                                  {isAllSelected
                                    ? `✓ ${selectableDocsInBucket.length}`
                                    : selectedDocsInBucket.length > 0
                                    ? `${selectedDocsInBucket.length}/${selectableDocsInBucket.length}`
                                    : selectableDocsInBucket.length}
                                </span>
                              )}
                            </div>
                            <div className="text-xs GREY_500-CLR tracking-wide leading-tight mt-1">
                              {summary.line1}
                            </div>
                            <div className="text-xs GREY_500-CLR tracking-wide leading-tight mt-1">
                              {summary.line2}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {hasAnyBucketSelected && (
                      <p className="text-xs text-blue-500 mt-2 font-medium">
                        Invoices selected for aging category
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          <div className="mt-0 pb-2 flex justify-between md:flex-wrap mb-4 lg:mb-2 ">
            <div className="flex z-20 items-center flex-wrap lg:flex-nowrap w-full ">
              <div
                className={`flex gap-3 w-full ${
                  selectedTabId === 'PAID' ? 'items-end' : 'items-center'
                }`}>
                {/* Search */}
                <div
                  className={twMerge(
                    'w-full sm:w-auto sm:min-w-[180px] lg:w-[250px]',
                    selectedTabId === 'PAID' ? 'sm:w-[150px]' : 'sm:flex-1 lg:flex-none'
                  )}>
                  <AppInputField
                    inputFieldStyles={{ padding: '6px 6px' }}
                    id="search"
                    placeholder={'Search '}
                    icon={<Search size={13} color={COLORS.GREY_500} />}
                    onTextChange={(text) => {
                      setSearchText(text);
                    }}
                  />
                </div>

                {/* Invoice Date (PAID tab) */}
                {selectedTabId === 'PAID' && (
                  <div className="flex items-end">
                    <div className="flex flex-col z-20 w-full sm:w-auto sm:min-w-[180px]">
                      <p className="text-2xs sm:text-xs font-bold pl-1 mb-0.5">Invoice Date</p>
                      <DillDropdown
                        placeholder="Invoice Date"
                        inputFieldStyles={{
                          padding: width <= smallDeviceWidth ? '2px 2px' : '',
                        }}
                        containerClassName={'h-[34px] justify-center'}
                        isSearchable={false}
                        isCreatable={false}
                        items={weeksOptions.map((opt) => ({
                          ...opt,
                          labelInElement: <div className="px-2 py-1 text-xs">{opt.label}</div>,
                        }))}
                        value={buyerSupplierInvoicesDataWeeksLimit.toString()}
                        onSelectChange={(data) => {
                          if (data?.value) {
                            dispatch(
                              updateBuyerSupplierInvoicesDataWeeksLimit(parseInt(data?.value))
                            );
                          }
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Invoice filter checkboxes (OPEN tab) */}
                {selectedTabId === 'OPEN' && (
                  <div className="flex flex-row flex-wrap gap-x-3 gap-y-1 items-center">
                    {invoicesFilterOptions.map((option, i) => {
                      return (
                        <div
                          key={`${i}-o`}
                          className="flex items-center gap-1 cursor-pointer"
                          onClick={() => {
                            dispatch(resetInvoiceIdsForDownload());
                            dispatch(resetSelectInvoice());
                            if (selectedInvoiceFilterId !== option.id) {
                              setSelectedInvoiceFilterId(option.id);
                            } else {
                              setSelectedInvoiceFilterId(null);
                            }
                          }}>
                          <DillCheckbox isChecked={selectedInvoiceFilterId === option.id} />
                          <p className="text-2xs">{option.name}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex ml-0 mt-0 lg:mt-0  lg:ml-auto items-start md:items-center flex-col sm:flex-row w-full lg:w-fit justify-between md:justify-between">
                <div className="flex  mt-4 sm:mt-0 ml-0 sm:ml-2 flex-col w-fit sm:w-fit items-end">
                  <div className="flex w-full md:w-fit justify-between sm:justify-normal gap-2">
                    {statementPdfsOptions.length > 0 && activeJobIds.length === 0 ? (
                      <DillDropdownButton
                        options={[...statementPdfsOptions]}
                        icon={<FilePlus size={19} />}
                        onSelect={(option) => {
                          if (option.value === 'TODAY') {
                            handleDownloadInvoiceStatements();
                          } else if (option.value) {
                            let fileName = option.extraData;
                            if (!fileName) {
                              const decodedUrl = decodeURIComponent(option.value);
                              const parts = decodedUrl.split('/');
                              fileName = `${parts[parts.length - 1]}.pdf`;
                            }
                            downloadFile(option.value, fileName);
                          }
                        }}
                        variant="secondary"
                        label="Download Statement"
                        height={32}
                      />
                    ) : (
                      <DillButton
                        label={
                          activeJobIds.length > 0 ? 'Download Job Statement' : 'Download Statement'
                        }
                        icon={<FilePlus size={19} />}
                        variant="secondary"
                        onClick={handleDownloadInvoiceStatements}
                        height={32}
                      />
                    )}
                    <div
                      className="relative mr-1 ml-1 sm:ml-auto"
                      data-tooltip-id={
                        Object.values(selectedInvoices).flat() &&
                        Object.values(selectedInvoices).flat().length < 1
                          ? 'buyer-bulk-download-tool-tip'
                          : ''
                      }>
                      {Object.values(invoiceIdsForDownload).flat().length === 1 ? (
                        <DillButton
                          onClick={() => {
                            if (
                              Object.values(invoiceIdsForDownload).flat() &&
                              Object.values(invoiceIdsForDownload).flat().length > 0
                            ) {
                              handleDownloadAll({ combined: true });
                            }
                          }}
                          variant="secondary"
                          label="Bulk Download"
                          height={32}
                        />
                      ) : (
                        <DillDropdownButton
                          options={[
                            { label: 'Separate files', value: 'separate' },
                            { label: 'One file', value: 'onefile' },
                          ]}
                          onSelect={(option) => {
                            if (
                              Object.values(invoiceIdsForDownload).flat() &&
                              Object.values(invoiceIdsForDownload).flat().length > 0
                            ) {
                              if (option.value === 'onefile') {
                                handleDownloadAll({ combined: true });
                              } else {
                                handleDownloadAll({ combined: false });
                              }
                            }
                          }}
                          variant="secondary"
                          label="Bulk Download"
                          height={32}
                        />
                      )}
                      <div
                        className={twMerge(
                          'flex absolute -top-2 right-[-12px] PRIMARY_500-BG WHITE-CLR rounded-full text-2xs font-bold h-6 w-6 items-center border border-white justify-center'
                        )}>
                        {Object.values(invoiceIdsForDownload).flat()?.length}
                      </div>
                      <ReactTooltip
                        id={'buyer-bulk-download-tool-tip'}
                        place="top"
                        variant="dark"
                        opacity={'100%'}
                        style={{
                          background: '#222',
                          width: '200px',
                          zIndex: 99999,
                        }}>
                        <span>Use checkboxes to select multiple invoices</span>
                      </ReactTooltip>
                    </div>

                    <div
                      className="relative grid ml-2"
                      data-tooltip-id={
                        !buyerUserCanPay
                          ? 'no_pay_permission'
                          : Object.values(selectedInvoices).flat() &&
                            Object.values(selectedInvoices).flat().length < 1
                          ? 'buyer-bulk-pay-tool-tip'
                          : ''
                      }>
                      <DillButton
                        label="Bulk Pay"
                        onClick={() => {
                          if (
                            Object.values(selectedInvoices).flat() &&
                            Object.values(selectedInvoices).flat().length > 0
                          ) {
                            handlePayBulkInvoice();
                          }
                        }}
                        height={32}
                        isDisabled={!selectedSupplier?.isFullyVerified || !buyerUserCanPay}
                      />

                      <div
                        className={twMerge(
                          'flex absolute -top-2 right-[-12px] PRIMARY_500-BG WHITE-CLR rounded-full text-2xs font-bold h-6 w-6 items-center border border-white justify-center '
                        )}>
                        {Object.values(selectedInvoices).flat().length +
                          Object.values(selectedCreditMemos).flat().length}
                      </div>
                      <div className="TEXT_SECONDARY-CLR mt-0.5 mb-0.5 mr-2 text-xs absolute -bottom-5 right-1">
                        {formatMoney(accumulateAmount, 2)}
                      </div>
                      <ReactTooltip
                        id={'buyer-bulk-pay-tool-tip'}
                        place="top"
                        variant="dark"
                        opacity={'100%'}
                        style={{
                          background: '#222',
                          width: '200px',
                          zIndex: 99999,
                        }}>
                        <span>Use checkboxes to select multiple invoices</span>
                      </ReactTooltip>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3  GREY_200-BORDER">
            {invoicesTabs.map((tab, index) => {
              const isFirst = index === 0;
              const isLast = index === invoicesTabs.length - 1;
              return (
                <div
                  key={index + 'oo'}
                  className={twMerge(
                    'flex flex-col cursor-pointer border-t overflow-x-hidden bg-white pt-1 WHITE-BG h-[64px] ',
                    isFirst && 'rounded-tl-xl border-l',
                    isLast && 'rounded-tr-xl',
                    selectedTabId === tab.id ? 'z-10' : 'hover:bg-blue-50 border-x GREY_200-BORDER'
                  )}
                  onClick={() => {
                    const searchParams = new URLSearchParams(location.search);
                    const invoiceId = searchParams.get('invoiceId');
                    if (invoiceId) {
                      navigate(location.pathname);
                    }
                    setSelectedTabId(tab.id);
                  }}>
                  <div className="h-full">
                    <p className="font-bold text-xs pl-4 mb-0 md:mb-1 px-2 mt-1 truncate mt-2">
                      {tab.name}
                    </p>
                    <div className="flex mb-1 justify-between px-4 mt-1">
                      <div className="flex items-center flex-col sm:flex-row ">
                        <div className="flex items-center">
                          <div
                            className={
                              'rounded-full p-1 h-1 w-1 md:h-2 md:w-2' +
                              (tab.id === 'PAST_DUE'
                                ? 'WARNING_500-BG '
                                : tab.id === 'CURRENT_DUE'
                                ? ' INFO_500-BG'
                                : tab.id === 'PAID'
                                ? ' SUCCESS_500-BG'
                                : tab.id === 'PENDING_PAY'
                                ? ' PRIMARY_500-BG'
                                : ' WARNING_500-BG')
                            }></div>
                          <p className="text-xs ml-1 font-semibold">{`$${roundTo(
                            tab.totalAmount,
                            2
                          )}`}</p>
                        </div>
                        <div className="flex items-center">
                          <p className="text-xs TEXT_SECONDARY-CLR mx-1 hidden sm:block">|</p>
                          <p className="text-xs TEXT_SECONDARY-CLR ">{tab.invoices.length} items</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedTabId === tab.id && (
                    <div className="h-0.5 md:h-1 w-full PRIMARY_500-BG"></div>
                  )}
                  {selectedTabId !== tab.id && (
                    <div className="h-0.5 md:h-1 w-full GREY_300-BG"></div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex-1 min-h-0 mb-4 ">
            {tableInvoices && selectedTabId && (
              <InvoicesTable
                invoices={searchedTableInvoices}
                selectedTabId={selectedTabId}
                onPaySingleInvoice={handlePaySingleInvoice}
                selectedInvoiceForPreview={invoiceForPreview}
                selectedCreditMemoForPreview={CreditMemoForPreview}
                accumulateAmount={accumulateAmount}
                selectedRowId={selectedRowId}
                onRowClick={(row) => setSelectedRowId(row.id)}
              />
            )}
          </div>
        </div>
      )}
      <ReactTooltip
        id={'no_pay_permission'}
        place="bottom"
        variant="dark"
        opacity={'100%'}
        style={{
          background: '#222',
          width: '300px',
          zIndex: 99999,
        }}>
        <span>You don't have permissions to make payments. Please contact your account admin.</span>
      </ReactTooltip>
    </div>
  );
};
